import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import moment from 'moment'
import { Box, Chip, FormControl, Grid, MenuItem, Select, Tooltip, Typography } from '@mui/material'
import WebService from '@util/webService'
import { findCode } from '@util/helper'
import useAlert from '@hooks/useAlert'

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토']
const SELECTED_REGI = ['S003', 'S005'] // 선발됨, 등록완료

// 출결 상태 분류
const kindOf = (statCode, date, today) => {
  if(statCode === 'S001') return 'present'
  if(statCode === 'S003' || statCode === 'S004') return 'late'
  if(statCode?.startsWith('S2')) return 'absent'
  if(statCode?.startsWith('H')) return 'off'
  // S000 미정
  return date >= today ? 'planned' : 'unchecked'
}

const chipStyles = {
  present: { color: '#1565c0', backgroundColor: '#e3f2fd', borderColor: '#90caf9' },
  late: { color: '#e65100', backgroundColor: '#fff3e0', borderColor: '#ffcc80' },
  absent: { color: '#c62828', backgroundColor: '#ffebee', borderColor: '#ef9a9a' },
  off: { color: '#9e9e9e', backgroundColor: '#f5f5f5', borderColor: '#e0e0e0', textDecoration: 'line-through' },
  planned: { color: '#555', backgroundColor: '#fff', borderColor: '#bdbdbd' },
  unchecked: { color: '#9e9e9e', backgroundColor: '#fff', borderColor: '#e0e0e0', borderStyle: 'dashed' },
}

const weekdayOf = date => WEEKDAYS[moment(date, 'YYYY-MM-DD').day()]

// 요일 순서: 월 ~ 일
const weekdayOrder = wd => (WEEKDAYS.indexOf(wd) + 6) % 7

const ManagerStudentSchedule = ({ student, regiAndLectureList }) => {
  const codes = useSelector(state => state.codeReducer)
  const { alert, renderAlert } = useAlert()
  const today = moment().format('YYYY-MM-DD')

  // 학기 선택 목록: 수강(신청) 정보에 있는 학기, 최신순
  const smsts = useMemo(() => {
    const map = new Map()
    regiAndLectureList.forEach(r => {
      if(r.smst && !map.has(r.smst.id)) {
        map.set(r.smst.id, r.smst)
      }
    })
    return [...map.values()].sort((a, b) => b.id - a.id)
  }, [regiAndLectureList])

  // 기본 학기: 선발/등록된 강좌가 있는 가장 최근 학기
  const defaultSmstId = useMemo(() => {
    const selected = regiAndLectureList
      .filter(r => SELECTED_REGI.includes(r.regi?.statCode))
      .map(r => r.smst.id)
      .sort((a, b) => b - a)
    return selected[0] ?? smsts[0]?.id ?? -1
  }, [regiAndLectureList, smsts])

  const [smstId, set_smstId] = useState(defaultSmstId)
  const [sessions, set_sessions] = useState([])
  const [pendings, set_pendings] = useState([])

  useEffect(() => {
    set_smstId(defaultSmstId)
  }, [defaultSmstId])

  const fetchSchedule = async () => {
    if(smstId < 0) {
      set_sessions([])
      set_pendings([])
      return
    }
    const url = `/bing/schedule/student/${student.schoolId}/${student.id}`
    const resp = await WebService.get(url, { smstId })
    if(resp.repCode !== 'ack') {
      alert(resp.repMessage)
      return
    }
    const repMessage = JSON.parse(resp.repMessage)
    set_sessions(JSON.parse(repMessage.sessions))
    set_pendings(JSON.parse(repMessage.pendings))
  }

  useEffect(() => {
    fetchSchedule()
  }, [smstId, student.id])

  // 강좌별로 회차를 묶고 출결 집계
  const lectureRows = useMemo(() => {
    const map = new Map()
    sessions.forEach(s => {
      const key = s.lecture.id
      if(!map.has(key)) {
        map.set(key, { lecture: s.lecture, offCode: s.offCode, sessions: [], times: new Map() })
      }
      const row = map.get(key)
      row.sessions.push(s)
      // 보강이 아닌 회차로 정규 시간표를 만듦
      if(s.subYn !== 'Y') {
        const wd = weekdayOf(s.date)
        const label = `${wd} ${s.startTime}~${s.endTime}`
        row.times.set(label, weekdayOrder(wd))
      }
    })

    return [...map.values()].map(row => {
      const count = { present: 0, late: 0, absent: 0, off: 0, planned: 0, unchecked: 0 }
      row.sessions.forEach(s => {
        count[kindOf(s.statCode, s.date, today)] += 1
      })
      const timeLabel = [...row.times.entries()]
        .sort((a, b) => a[1] - b[1])
        .map(([label]) => label)
        .join(', ')
      return { ...row, count, timeLabel }
    }).sort((a, b) => a.lecture.title.localeCompare(b.lecture.title))
  }, [sessions])

  const offName = code => findCode(codes, 'SA29', code)?.name ?? '-'

  const pendingTimeLabel = lschs => (lschs ?? [])
    .map(l => `${findCode(codes, 'SB07', l.weekCode)?.name?.charAt(0) ?? ''} ${l.startTime}~${l.endTime}`)
    .join(', ')

  const renderCount = (label, value, color) => (
    value > 0 ? (
      <span style={{ marginRight: 10, whiteSpace: 'nowrap' }}>
        {label} <b style={{ color }}>{value}</b>
      </span>
    ) : null
  )

  const renderSessionChip = s => {
    const kind = kindOf(s.statCode, s.date, today)
    const statName = kind === 'planned' ? '예정'
      : kind === 'unchecked' ? '미입력'
      : (findCode(codes, 'SA10', s.statCode)?.name ?? s.statCode)
    const chgName = s.chgCode && s.chgCode !== 'C000' ? findCode(codes, 'SB10', s.chgCode)?.name : null
    const tips = [
      `${s.date}(${weekdayOf(s.date)}) ${s.startTime}~${s.endTime}`,
      statName,
      s.subYn === 'Y' ? `보강 (원래 ${s.orgDate})` : null,
      chgName ? `이동: ${chgName}` : null,
    ].filter(Boolean)

    return (
      <Tooltip key={s.id} title={<span style={{ whiteSpace: 'pre-line' }}>{tips.join('\n')}</span>} arrow>
        <Chip
          size="small"
          variant="outlined"
          label={`${moment(s.date, 'YYYY-MM-DD').format('M/D')} ${statName}${s.subYn === 'Y' ? '·보강' : ''}`}
          sx={{
            ...chipStyles[kind],
            fontSize: '0.75rem',
            '& .MuiChip-label': { lineHeight: 1, paddingTop: '2px' },
          }}
        />
      </Tooltip>
    )
  }

  if(smsts.length === 0) {
    return null
  }

  return (
    <Box sx={{ mt: 1, width: '100%' }}>
      {renderAlert()}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography component="h1" variant="h5">
          수업 일정 · 출결
        </Typography>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <Select value={smstId} onChange={e => set_smstId(+e.target.value)}>
            {smsts.map(s => (
              <MenuItem key={s.id} value={s.id}>{s.year} / {s.gisu}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Grid container sx={{ bgcolor: '#fafafa', height: 30, display: 'flex', alignItems: 'center' }}>
        <Grid item xs={2}>강좌</Grid>
        <Grid item xs={3}>요일 · 시간</Grid>
        <Grid item xs={2}>귀가방법</Grid>
        <Grid item xs={5}>출결</Grid>
      </Grid>

      {lectureRows.length === 0 && pendings.length === 0 ? (
        <Typography variant="body2" sx={{ color: '#999', py: 1 }}>
          선택한 학기에 선발/등록된 강좌가 없습니다.
        </Typography>
      ) : null}

      {lectureRows.map(row => (
        <Box key={row.lecture.id} sx={{ borderBottom: '1px solid #f0f0f0', pb: 1 }}>
          <Grid container sx={{ fontSize: '0.9em', display: 'flex', alignItems: 'center', minHeight: 34 }}>
            <Grid item xs={2}>{row.lecture.title}</Grid>
            <Grid item xs={3}>{row.timeLabel || '-'}</Grid>
            <Grid item xs={2}>{offName(row.offCode)}</Grid>
            <Grid item xs={5}>
              {renderCount('출석', row.count.present, chipStyles.present.color)}
              {renderCount('지각/조퇴', row.count.late, chipStyles.late.color)}
              {renderCount('결석', row.count.absent, chipStyles.absent.color)}
              {renderCount('휴강', row.count.off, chipStyles.off.color)}
              {renderCount('미입력', row.count.unchecked, chipStyles.unchecked.color)}
              {renderCount('남은 회차', row.count.planned, chipStyles.planned.color)}
            </Grid>
          </Grid>
          {/* 회차별 출결은 항상 표시 */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {row.sessions.map(renderSessionChip)}
          </Box>
        </Box>
      ))}

      {pendings.map(p => (
        <Grid container key={`pending-${p.id}`} sx={{ fontSize: '0.9em', display: 'flex', alignItems: 'center', minHeight: 34, borderBottom: '1px solid #f0f0f0' }}>
          <Grid item xs={2}>{p.lecture.title}</Grid>
          <Grid item xs={3}>{pendingTimeLabel(p.lschs) || '-'}</Grid>
          <Grid item xs={2}>{offName(p.offCode)}</Grid>
          <Grid item xs={5} sx={{ color: '#888' }}>
            개강 예정{p.lectureStime ? ` (${moment(p.lectureStime, 'YYYY-MM-DD').format('M/D')})` : ''} · 출석부 생성 전
          </Grid>
        </Grid>
      ))}
    </Box>
  )
}

export default ManagerStudentSchedule
